import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { getUnclaimedParishes, claimParish } from '../lib/api';
import { 
  Search, 
  MapPin, 
  Church, 
  Clock, 
  Phone, 
  Globe,
  CheckCircle,
  Loader2,
  Send,
  Filter
} from 'lucide-react';

export default function ClaimParishPage() {
  const [parishes, setParishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dioceseFilter, setDioceseFilter] = useState('');
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [selectedParish, setSelectedParish] = useState(null);
  const [claimMessage, setClaimMessage] = useState('');
  const [claimRole, setClaimRole] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadParishes();
  }, [dioceseFilter]);

  const loadParishes = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dioceseFilter) params.diocese = dioceseFilter;
      
      const response = await getUnclaimedParishes(params);
      setParishes(response.data.parishes || []);
    } catch (error) {
      console.error('Failed to load parishes:', error);
      toast.error('Failed to load parishes');
    }
    setLoading(false);
  };

  const openClaimDialog = (parish) => {
    setSelectedParish(parish);
    setClaimMessage('');
    setClaimRole('');
    setClaimDialogOpen(true);
  };

  const handleSubmitClaim = async () => {
    if (!claimMessage.trim()) {
      toast.error('Please provide a message explaining why you should manage this parish');
      return;
    }

    setSubmitting(true);
    try {
      await claimParish(selectedParish.parish_id, claimMessage, claimRole);
      toast.success('Claim submitted! A site administrator will review your request.');
      setClaimDialogOpen(false);
      
      // Update the parish in the list to show pending claim
      setParishes(prev => prev.map(p => 
        p.parish_id === selectedParish.parish_id 
          ? { ...p, has_pending_claim: true }
          : p
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit claim');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#F5F3EE] py-8">
      <div className="max-w-5xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#2C2925] font-['Nunito']">
            Claim Your Parish
          </h1>
          <p className="text-[#6B665E] mt-2">
            Browse parishes imported from MassTimes.org and request to become an administrator
          </p>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex gap-4 items-center">
              <Filter className="w-5 h-5 text-[#6B665E]" />
              <Input
                placeholder="Filter by diocese..."
                value={dioceseFilter}
                onChange={(e) => setDioceseFilter(e.target.value)}
                className="max-w-xs"
              />
              {dioceseFilter && (
                <Button variant="ghost" size="sm" onClick={() => setDioceseFilter('')}>
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Parishes List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Church className="w-5 h-5" />
              Available Parishes
            </CardTitle>
            <CardDescription>
              {parishes.length} unclaimed parishes
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#8B7355]" />
              </div>
            ) : parishes.length === 0 ? (
              <div className="text-center py-12 text-[#6B665E]">
                <Church className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">No unclaimed parishes found</p>
                <p className="text-sm mt-2">Check back later as more parishes are imported</p>
              </div>
            ) : (
              <div className="space-y-4">
                {parishes.map((parish) => (
                  <div 
                    key={parish.parish_id}
                    className="p-4 border rounded-lg bg-white hover:border-[#8B7355] transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-lg text-[#2C2925]">{parish.name}</h3>
                          {parish.has_pending_claim && (
                            <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                              <Clock className="w-3 h-3 mr-1" />
                              Claim Pending
                            </Badge>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-[#6B665E]">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 flex-shrink-0" />
                            <span>{parish.address || parish.location}</span>
                          </div>
                          {parish.diocese_name && (
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Diocese:</span>
                              <span>{parish.diocese_name}</span>
                            </div>
                          )}
                          {parish.office_phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4" />
                              <span>{parish.office_phone}</span>
                            </div>
                          )}
                          {parish.website && (
                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4" />
                              <a 
                                href={parish.website.startsWith('http') ? parish.website : `https://${parish.website}`}
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline truncate"
                              >
                                {parish.website}
                              </a>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span>{parish.mass_schedules?.length || 0} Mass times imported</span>
                          </div>
                        </div>
                      </div>
                      
                      <Button
                        className="ml-4 bg-[#8B7355] hover:bg-[#6B5A45]"
                        disabled={parish.has_pending_claim}
                        onClick={() => openClaimDialog(parish)}
                        data-testid={`claim-parish-${parish.parish_id}`}
                      >
                        {parish.has_pending_claim ? (
                          <>
                            <Clock className="w-4 h-4 mr-2" />
                            Pending
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Claim Parish
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Claim Dialog */}
        <Dialog open={claimDialogOpen} onOpenChange={setClaimDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Church className="w-5 h-5" />
                Claim {selectedParish?.name}
              </DialogTitle>
              <DialogDescription>
                Submit a request to become the administrator of this parish. A site admin will review your request.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-[#2C2925] block mb-2">
                  Your Role at This Parish
                </label>
                <Select value={claimRole} onValueChange={setClaimRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your role..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pastor">Pastor</SelectItem>
                    <SelectItem value="Parochial Vicar">Parochial Vicar</SelectItem>
                    <SelectItem value="Deacon">Deacon</SelectItem>
                    <SelectItem value="Parish Staff">Parish Staff</SelectItem>
                    <SelectItem value="Parish Council Member">Parish Council Member</SelectItem>
                    <SelectItem value="Volunteer Coordinator">Volunteer Coordinator</SelectItem>
                    <SelectItem value="Active Parishioner">Active Parishioner</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-[#2C2925] block mb-2">
                  Why should you manage this parish? <span className="text-red-500">*</span>
                </label>
                <Textarea
                  placeholder="Please explain your connection to this parish and why you should be granted administrator access..."
                  value={claimMessage}
                  onChange={(e) => setClaimMessage(e.target.value)}
                  rows={4}
                />
                <p className="text-xs text-[#6B665E] mt-1">
                  This message will be sent to site administrators for review
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setClaimDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitClaim}
                disabled={submitting || !claimMessage.trim()}
                className="bg-[#8B7355] hover:bg-[#6B5A45]"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Submit Claim
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
